import { useCallback, useState, useEffect, useRef } from 'react';
import {
  useAdminSearchPostsQuery,
  usePinPostMutation,
  useUnpinPostMutation,
  PostResponseSchema,
} from '@/api/client';
import { useAdminContext } from '../contexts/AdminProvider';

function extractErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    // RTK Query error format
    if ('data' in error && error.data && typeof error.data === 'object') {
      const data = error.data as Record<string, unknown>;
      if (typeof data.detail === 'string') {
        return data.detail;
      }
    }
    // Standard error
    if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
      return (error as { message: string }).message;
    }
  }
  return 'An unexpected error occurred';
}

const PAGE_SIZE = 50;

export function usePinnedPosts(skip: boolean = false) {
  const { showError } = useAdminContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [allUnpinnedPosts, setAllUnpinnedPosts] = useState<PostResponseSchema[]>([]);
  const prevSearchQueryRef = useRef(searchQuery);

  // Query 1: Always fetch ALL pinned posts (no pagination needed, usually small)
  const {
    data: pinnedData,
    isLoading: isPinnedLoading,
    isFetching: isPinnedFetching,
    refetch: refetchPinned,
  } = useAdminSearchPostsQuery(
    { pinnedOnly: true, size: 100 },
    { skip }
  );

  // Query 2: Paginated search for all/filtered posts (excludes pinned in display)
  const {
    data: searchData,
    isLoading: isSearchLoading,
    isFetching: isSearchFetching,
    refetch: refetchSearch,
  } = useAdminSearchPostsQuery(
    { q: searchQuery || undefined, page, size: PAGE_SIZE },
    { skip }
  );

  // Reset pagination when search query changes
  useEffect(() => {
    if (prevSearchQueryRef.current !== searchQuery) {
      prevSearchQueryRef.current = searchQuery;
      setPage(1);
      setAllUnpinnedPosts([]);
    }
  }, [searchQuery]);

  // Accumulate unpinned posts when new data arrives
  useEffect(() => {
    if (searchData?.items) {
      // Filter out pinned posts from the search results
      const unpinned = searchData.items.filter((p) => p.pin_order == null);
      if (page === 1) {
        setAllUnpinnedPosts(unpinned);
      } else {
        setAllUnpinnedPosts((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newPosts = unpinned.filter((p) => !existingIds.has(p.id));
          return [...prev, ...newPosts];
        });
      }
    }
  }, [searchData?.items, page]);

  const hasMore = (() => {
    if (!searchData || searchData.page == null || searchData.pages == null) return false;
    return searchData.page < searchData.pages;
  })();

  const loadMore = useCallback(() => {
    if (hasMore && !isSearchFetching) {
      setPage((p) => p + 1);
    }
  }, [hasMore, isSearchFetching]);

  // Pinned posts from dedicated query, sorted by pin_order
  const pinnedPosts = (pinnedData?.items ?? []).slice().sort(
    (a, b) => (a.pin_order ?? 0) - (b.pin_order ?? 0)
  );

  const [pinPostMutation, { isLoading: isPinning }] = usePinPostMutation();
  const [unpinPostMutation, { isLoading: isUnpinning }] = useUnpinPostMutation();
  const [isReordering, setIsReordering] = useState(false);

  const refetchAll = useCallback(() => {
    refetchPinned();
    refetchSearch();
  }, [refetchPinned, refetchSearch]);

  const pinPost = useCallback(
    async (postId: number, pinOrder?: number) => {
      try {
        // If no pinOrder specified, append to end of pinned list
        const order = pinOrder ?? pinnedPosts.length;
        await pinPostMutation({ postId, pinOrder: order }).unwrap();
        refetchAll();
      } catch (error) {
        console.error('Failed to pin post:', error);
        showError('Failed to pin post', extractErrorMessage(error));
      }
    },
    [pinPostMutation, refetchAll, pinnedPosts.length, showError]
  );

  const unpinPost = useCallback(
    async (postId: number) => {
      try {
        await unpinPostMutation(postId).unwrap();
        refetchAll();
      } catch (error) {
        console.error('Failed to unpin post:', error);
        showError('Failed to unpin post', extractErrorMessage(error));
      }
    },
    [unpinPostMutation, refetchAll, showError]
  );

  const reorderPinnedPosts = useCallback(
    async (reorderedPosts: PostResponseSchema[]) => {
      setIsReordering(true);
      try {
        // Build update list with new positions
        const updates = reorderedPosts.map((post, index) => ({
          postId: post.id,
          newOrder: index,
          currentOrder: post.pin_order,
        }));

        console.log('[reorderPinnedPosts] Updates:', updates);

        // Only update posts whose order actually changed
        const changedPosts = updates.filter((u) => u.currentOrder !== u.newOrder);
        console.log('[reorderPinnedPosts] Changed posts:', changedPosts);

        if (changedPosts.length === 0) {
          console.log('[reorderPinnedPosts] No changes needed');
          return;
        }

        // Unpin all changed posts first to avoid conflicts
        console.log('[reorderPinnedPosts] Unpinning changed posts...');
        for (const update of changedPosts) {
          console.log(`[reorderPinnedPosts] Unpinning post ${update.postId}`);
          await unpinPostMutation(update.postId).unwrap();
        }

        // Re-pin in the new order
        console.log('[reorderPinnedPosts] Re-pinning with new order...');
        for (const update of changedPosts) {
          console.log(`[reorderPinnedPosts] Pinning post ${update.postId} at order ${update.newOrder}`);
          await pinPostMutation({ postId: update.postId, pinOrder: update.newOrder }).unwrap();
        }

        console.log('[reorderPinnedPosts] Done, refreshing...');
        refetchAll();
      } catch (error) {
        console.error('Failed to reorder pinned posts:', error);
        showError('Failed to reorder posts', extractErrorMessage(error));
        refetchAll(); // Refresh to get actual state
      } finally {
        setIsReordering(false);
      }
    },
    [pinPostMutation, unpinPostMutation, refetchAll, showError]
  );

  const handleRefetch = useCallback(() => {
    setPage(1);
    setAllUnpinnedPosts([]);
    refetchAll();
  }, [refetchAll]);

  return {
    searchQuery,
    setSearchQuery,
    searchResults: allUnpinnedPosts,
    pinnedPosts,
    isSearching: isSearchLoading || isPinnedLoading,
    isFetching: isSearchFetching || isPinnedFetching,
    isPinning,
    isUnpinning,
    isReordering,
    pinPost,
    unpinPost,
    reorderPinnedPosts,
    refetch: handleRefetch,
    hasMore,
    loadMore,
  };
}
